/**
 * Kloutforce mirror of apps/katalog-app/src/components/SignupIntentHealer.tsx.
 *
 * Fixes the guest-order convergence gap: a Shopify guest purchase leaves
 * a paid order attached to the guest sentinel and a matching
 * account_signup_intents row. If the customer completes email
 * verification / sign-in on Kloutforce instead of Katalog, the Katalog
 * healer never runs and the order stays under the guest sentinel.
 *
 * This mirror fires ONE idempotent POST /api/account-signup/finalize
 * per authenticated Kloutforce session so the canonical convergence RPC
 * reassigns eligible paid orders to the authenticated user. Backend
 * contract is unchanged.
 *
 * Guards:
 *   - Runs ONLY when authReady && authUser.
 *   - Per-user one-shot via sessionStorage.
 *   - Failures are silent (no UX surface); no tokens are ever logged.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '@bright-web-skeleton/auth';
import { supabase } from '@bright-web-skeleton/supabase';
import { getBackendUrl } from '../lib/env';
import { finalizeSignupIntentWithRetry } from '../lib/signupIntentFinalize';

const STORAGE_KEY_PREFIX = 'kloutforce:signupIntentHealed:';

// Only outcomes that mean "nothing more can ever come of this" are
// terminal. Everything else (verified/already_verified with no attach,
// waiting_for_paid_order, no_intent, transient_exhausted, http_error)
// must remain retriable: a Shopify webhook may arrive later in the same
// browser session and create the paid order / intent that this run did
// not find. Marking those as done would strand the guest order.
const TERMINAL_OUTCOMES = new Set([
  'attached',
  'already_attached',
  'unauthorized',
  'conflict',
]);

export function SignupIntentHealer(): null {
  const { authReady, authUser } = useAuth();
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!authReady || !authUser || inFlightRef.current) return;
    const sessionFlag = `${STORAGE_KEY_PREFIX}${authUser.id}`;
    try {
      if (sessionStorage.getItem(sessionFlag)) return;
    } catch { /* storage unavailable — still attempt once */ }
    inFlightRef.current = true;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const outcome = await finalizeSignupIntentWithRetry({
          backendUrl: getBackendUrl(),
          accessToken: token,
          authUserId: authUser.id,
        });
        if (TERMINAL_OUTCOMES.has(outcome.kind)) {
          try { sessionStorage.setItem(sessionFlag, '1'); } catch { /* ignore */ }
        }
        console.warn('[SignupIntentHealer] signup_intent_heal_outcome', {
          user_id: authUser.id,
          outcome: outcome.kind,
          attempts: outcome.attempts,
          intent_id: outcome.intentId ?? null,
        });
      } catch {
        console.warn('[SignupIntentHealer] heal_unexpected_error', { user_id: authUser.id });
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [authReady, authUser]);

  return null;
}